import {Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {SelectionModel} from "@angular/cdk/collections";
import {ResultModel} from "../models/resultModel";
import {AuthService} from "../services/auth.service";
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {GeneralRequestModel} from "../models/generalRequestModel";
import {MatDialog} from "@angular/material/dialog";
import {ResultDialogComponent} from "../result-dialog/result-dialog.component";
import {ResultService} from "../services/result.service";
import {DateUtil} from "../util/date.util";
import {ApiResponse} from "../models/ApiResponse";
import {MatTableDataSource} from "@angular/material/table";
import {ResultsDatabaseFactory} from "./results-database.factory";

@Component({
  selector: 'app-results-database',
  templateUrl: './results-database.component.html',
  styleUrls: ['./results-database.component.css']
})
export class ResultsDatabaseComponent implements OnInit {

  isMobile: boolean = false;
  currentUserId: string | null;
  resultsTotalCount: number = 0;
  results: Array<ResultModel> = [];
  selectedResults: SelectionModel<ResultModel> = new SelectionModel<ResultModel>(true, []);
  paginationModel: GeneralRequestModel = {page: 1, count: 10, sortDirection: 'desc'};
  searchTerm: string = '';
  searchResults: Array<ResultModel> = [];
  showFilter?: boolean = false;
  selectedQuizId!: string;
  selectedResult!: string;
  isSelectResultsOpened: boolean = false;
  showSearchMobile: boolean = false;
  selectedTestCategoryId!: string;
  sortField: string = 'endTime';
  displayedColumns: string[] = ResultsDatabaseFactory.DISPLAYED_COLUMNS;
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);

  private categoryId!: string;
  private quizId!: string;

  @ViewChild('windowRef') windowRef?: ElementRef;
  @ViewChild('filterIcon') filterIcon?: ElementRef;
  @ViewChild('filterContainer') filterContainer?: ElementRef;

  constructor(
    private readonly resultService: ResultService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute
  ) {
    this.currentUserId = this.authService.getCurrentUserId();
  }

  ngOnInit(): void {
    this.initQueryParams();
    this.resultService.updatedEvent.subscribe({
      next: (): void => {
        this.handleResultRetrieval();
      }
    })
    this.initIsMobile();
  }

  onSearch() {
    if (this.searchTerm.length) {
      this.showSearchMobile = true;
      this.doSearchResults();
      this.isSelectResultsOpened = this.searchResults.length > 0;
    } else if (!this.searchTerm) {
      this.getResults();
      this.isSelectResultsOpened = false;
      this.showSearchMobile = false;
    }
  }

  onFilter(): void {
    this.showFilter = false;
    this.onSelectQuiz(this.quizId);
    this.onSelectCategory(this.categoryId);
    if (this.selectedQuizId || this.selectedTestCategoryId) {
      this.getResultsByAdminIdQuizIdAndCategoryId();
    } else {
      this.getResults();
    }
  }

  onSelectQuiz(quizId: string) {
    if (quizId && quizId !== "Test Name") {
      this.selectedQuizId = quizId;
    } else {
      this.selectedQuizId = '';
    }
  }

  onSelectCategory(categoryId: string) {
    if (categoryId && categoryId !== "Test Category") {
      this.selectedTestCategoryId = categoryId;
    } else {
      this.selectedTestCategoryId = '';
    }
  }

  goToRespondentResult(result: ResultModel) {
    this.router.navigate(['respondent-result'], {
      queryParams: {
        quizId: result.quizId,
        respondentId: result.respondentId,
      }
    }).then();
  }

  isAllSelected() {
    const numSelected = this.selectedResults.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selectedResults.clear();
      return;
    }
    this.selectedResults.select(...this.dataSource.data);
  }

  checkboxLabel(event: MouseEvent, row: ResultModel): void {
    event.stopPropagation();
    this.selectedResults.toggle(row);
  }

  openDeleteDialog(resultsForDelete: ResultModel[]) {
    this.dialog.open(ResultDialogComponent, {
      width: '620px',
      data: {
        showDeleteDialog: true,
        selectedResultsForDelete: resultsForDelete
      }
    });
  }

  toggleFilter() {
    this.showFilter = !this.showFilter;
  }

  resetFilter() {
    if (this.selectedQuizId || this.selectedTestCategoryId) {
      this.selectedTestCategoryId = this.selectedQuizId = '';
      this.getResults();
    }
    this.showFilter = false;
  }

  nextPage() {
    const pageMaxPossibleValue = Math.ceil(this.resultsTotalCount / this.paginationModel.count);
    if (this.paginationModel.page < pageMaxPossibleValue) {
      this.paginationModel.page++;
      this.handleResultRetrieval();
    }
  }

  previousPage() {
    if (this.paginationModel.page > 1) {
      this.paginationModel.page--;
      this.handleResultRetrieval();
    }
  }

  goToPageNumber(page: number) {
    if (page > 0 && page <= this.paginationModel.count) {
      this.paginationModel.page = page;
      this.getResults();
    }
  }

  selectTest(searchTerm: string) {
    this.handleSearch(searchTerm);
    this.selectedResult = searchTerm;
    this.isSelectResultsOpened = false;
    this.showSearchMobile = false;
  }

  handleSearch(searchTerm: string) {
    this.searchTerm = searchTerm;
    this.onSearch();
  }

  @HostListener('document:click', ['$event'])
  handleClickOutsideForm(event: Event): void {
    if (this.showFilter && !this.windowRef?.nativeElement.contains(event.target)
      && !this.filterIcon?.nativeElement.contains(event.target) &&
      !this.filterContainer?.nativeElement.contains(event.target)) {
      this.showFilter = false;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.initIsMobile();
  }

  private getResultsByAdminIdQuizIdAndCategoryId() {
    this.resultService.getResultsByAdminIdQuizIdAndCategoryId(
      this.currentUserId, this.paginationModel, this.selectedQuizId, this.selectedTestCategoryId
    ).subscribe({
      next: (results: ApiResponse<ResultModel>) => {
        this.initializeProperties(results);
        this.showFilter = false;
        this.searchTerm = '';
      }
    });
  }

  private initializeProperties(results: ApiResponse<ResultModel>) {
    this.dataSource.data = results.data;
    this.searchResults = results.data;
    this.dataSource.data.forEach(item => {
      item.endTime = DateUtil.convertUTCtoLocal(item.endTime);
    });
    this.resultsTotalCount = results.meta.total;
    this.selectedResults.clear();
  }

  private doSearchResults() {
    this.resultService.searchResults(
      this.currentUserId, this.paginationModel, this.searchTerm.trim()
    ).subscribe({
      next: (results: ApiResponse<ResultModel>) => {
        this.initializeProperties(results);
        this.resetFilter();
      }
    });
  }

  private handleResultRetrieval() {
    if (this.selectedQuizId?.trim() || this.selectedTestCategoryId?.trim()) {
      this.onFilter();
    } else if (this.searchTerm.trim().length > 2) {
      this.onSearch();
    } else {
      this.getResults();
    }
  }

  private initQueryParams() {
    this.route.queryParamMap.subscribe({
      next: (params: ParamMap) => {
        this.categoryId = params.get('categoryId') ?? '';
        this.quizId = params.get('quizId') ?? '';
        this.onFilter();
      }
    });
  }

  private initIsMobile() {
    const screenWidth = window.innerWidth;
    this.isMobile = screenWidth < 1221;
  }

  private getResults() {
    this.resultService.getResultsByAdminId(this.currentUserId, this.paginationModel, this.sortField).subscribe(results => {
      this.initializeProperties(results);
    })
  }

  protected readonly Math = Math;
}

